import { useEffect, useState } from 'react';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Alert, Box, Button, CircularProgress, Stack } from '@mui/material';
import { useParams, useSearchParams } from 'react-router-dom';

import MatchView from '../../components/live/match/MatchView';
import { getDirectMatch, getPaginatedDirectMatch } from '../../../api';

export default function DirectMatchPage() {
  const [reloadKey, setReloadKey] = useState(0);

  const { matchId } = useParams();
  const [searchParams] = useSearchParams();

  const widgetConfigurationId = searchParams.get('widgetConfigurationId') || null;

  const day = searchParams.get('day') || null;

  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let ignore = false;

    const loadMatch = async () => {
      if (!matchId) {
        setMatch(null);
        setError('Match introuvable.');
        return;
      }

      try {
        setLoading(true);
        setError('');

        const { data } = await getDirectMatch({
            matchId,
            day,
            widgetConfigurationId,
          });

        if (!ignore) {
          setMatch(data);
        }
      } catch (e) {
        if (!ignore) {
          setMatch(null);
          setError('Impossible de charger le direct du match.');
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    loadMatch();

    return () => {
      ignore = true;
    };
  }, [matchId, day, widgetConfigurationId, reloadKey]);

  const loadMore = async () => {
    if (!match?.idMatch || match?.pagination == null || loadingMore) {
      return;
    }

    try {
      setLoadingMore(true);

      const { data } = await getPaginatedDirectMatch({
        matchId: match.idMatch,
        pagination: match.pagination,
        day: match.selectedDay || day,
        widgetConfigurationId,
        });

      setMatch((current) => {
        if (!current) return data;

        return {
          ...current,
          ...data,
          articles: [
            ...(current.articles || []),
            ...(data.articles || []),
          ],
          notations: [
            ...(current.notations || []),
            ...(data.notations || []),
          ],
          surveys: [
            ...(current.surveys || []),
            ...(data.surveys || []),
          ],
          quizzs: [
            ...(current.quizzs || []),
            ...(data.quizzs || []),
          ],
          questionBoxes: [
            ...(current.questionBoxes || []),
            ...(data.questionBoxes || []),
          ],
          hasMore: data.hasMore,
          pagination: data.pagination,
          dayFeed: data.dayFeed || current.dayFeed,
          availableDays: data.availableDays || current.availableDays,
          selectedDay: data.selectedDay || current.selectedDay,
          selectedDayCount: data.selectedDayCount ?? current.selectedDayCount,
          hasPreviousDay: data.hasPreviousDay ?? current.hasPreviousDay,
          hasNextDay: data.hasNextDay ?? current.hasNextDay,
        };
      });
    } catch (e) {
      setError('Impossible de charger la suite du direct.');
    } finally {
      setLoadingMore(false);
    }
  };

  if (loading && !match) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !match) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!match) {
    return null;
  }

  return (
    <Box sx={{ width: '100%', bgcolor: '#fff' }}>
      {error ? (
        <Alert severity="warning" sx={{ m: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Stack
        direction="row"
        justifyContent="flex-end"
        sx={{ p: 1 }}
      >
        <Button
          size="small"
          variant="outlined"
          startIcon={<RefreshIcon />}
          disabled={loading}
          onClick={() => setReloadKey((value) => value + 1)}
        >
          Actualiser
        </Button>
      </Stack>

      <MatchView match={match} loadMore={loadMore} />
    </Box>
  );
}